
import React from "react";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCountyFAQs } from "@/lib/county-faqs";
import { generateBreadcrumbSchema, generateFAQSchema, generateHowToSchema, generateLocalBusinessSchema } from "@/lib/schema";

interface CountySEOProps {
  countyName: string;
  countySlug: string | undefined;
  nursingHomeCount: number;
  breadcrumbs: { name: string; url: string }[];
}

export function CountySEO({ countyName, countySlug, nursingHomeCount, breadcrumbs }: CountySEOProps) {
  // Get current year for dynamic title
  const currentYear = new Date().getFullYear();

  // Get county-specific FAQs
  const countyFaqs = getCountyFAQs(countySlug || '');

  // Generate schemas for structured data
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  const faqSchema = generateFAQSchema(countyFaqs);
  
  // Add HowTo schema for finding nursing homes process
  const howToSchema = generateHowToSchema({
    name: `How to Find a Nursing Home in ${countyName}`,
    description: `Follow these steps to find the best nursing home in ${countyName} for your loved one.`,
    steps: [
      {
        name: "Research Options",
        text: `Browse our comprehensive list of nursing homes in ${countyName} to understand your options.`
      },
      {
        name: "Compare Facilities",
        text: "Compare amenities, services, and pricing across different nursing homes."
      },
      {
        name: "Check Availability",
        text: "Contact nursing homes directly or use our platform to check current availability."
      },
      {
        name: "Visit in Person",
        text: "Schedule visits to your shortlisted nursing homes to see them firsthand."
      },
      {
        name: "Understand Funding Options",
        text: "Learn about the Fair Deal scheme and other financial options for nursing home care."
      },
      {
        name: "Make Your Decision",
        text: "Choose the nursing home that best meets your loved one's needs and preferences."
      }
    ]
  });
  
  // Add LocalBusiness schema for the county
  const localBusinessSchema = generateLocalBusinessSchema({
    name: `Nursing Homes in ${countyName}`,
    description: `Find and compare quality nursing homes and care facilities in ${countyName}, Ireland.`,
    url: `https://www.grandpal.co/nursing-homes/${countySlug}`,
    telephone: "+353-1-234-5678",
    address: {
      addressCountry: "Ireland",
      addressRegion: countyName
    }
  });
  
  // Combine all schemas
  const schemas = [breadcrumbSchema, faqSchema, howToSchema, localBusinessSchema];

  // Generate SEO metadata
  const metaTitle = `Top Nursing Homes in ${countyName} (${currentYear}) | Compare ${nursingHomeCount}+ Homes – Grandpal`;
  
  const metaDescription = `Compare ${nursingHomeCount}+ nursing homes in ${countyName}. Browse reviews, pricing, availability, and HSE Fair Deal eligibility. Let Grandpal help you find the best care for your loved one.`;

  return (
    <SEOHead 
      title={metaTitle}
      description={metaDescription}
      canonicalUrl={`/nursing-homes/${countySlug}`}
      schema={schemas}
    />
  );
}
